import styled, { css } from 'styled-components'

import * as breakpoints from '../constants/breakpoints'
import * as colors from '../constants/colors'
import * as fonts from '../constants/fonts'
import * as glyphs from '../icons/glyphs'
import * as typography from '../constants/neue/typography'

import * as copy from './copy'
import * as heading from './heading'

import { Spinner } from './spinner'
import { Tooltip } from './tooltip'

export const ErrorMessage = styled.span`
  ${typography.COPY_XS_REGULAR};
  color: ${colors.PRIMARY_RED};
  position: relative;
  top: 6px;
`

export const Explanations = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  transition: width 1s cubic-bezier(0.16, 1, 0.3, 1);
  white-space: nowrap;
  width: 100%;

  @media screen and ${breakpoints.TABLET} {
    padding-top: 0;
    width: 50%;
  }
`

export const Preview = styled.div<{ $visible: boolean }>`
  align-items: center;
  border-bottom: 1px solid ${colors.PRIMARY_SAND};
  display: flex;
  flex-direction: column;
  width: 0;

  ${({ $visible }) => {
    if ($visible) {
      return css`
        margin-bottom: 30px;
        padding-bottom: 30px;
        width: 100%;
      `
    }
  }}
  img {
    max-width: 100%;
    max-height: 200px;

    @media screen and ${breakpoints.TABLET} {
      max-height: 162px;
    }
  }

  @media screen and ${breakpoints.TABLET} {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;

    ${({ $visible }) => {
      if ($visible) {
        return css`
          border-right: 1px solid ${colors.PRIMARY_SAND};
          margin-right: 25px;
          padding-right: 25px;
          width: 50%;
        `
      }
    }}
  }
`

export const MidText = styled(copy.S.regular)`
  color: inherit;
  margin-bottom: 20px;
`

export const RemoveImage = styled.button<{ $disabled: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  margin-top: 8px;
  position: relative;

  ${copy.XS.regular} {
    color: ${colors.GREY_SHADE_2};
  }

  ${glyphs.Icon} {
    color: ${colors.GREY_SHADE_2};
    margin-right: 5px;
  }

  ${Tooltip} {
    display: none;
    position: absolute;
    right: 10%;
    top: calc(100% + 14px);
  }

  ${({ disabled }) => {
    if (disabled)
      return css`
        cursor: not-allowed;

        &:hover {
          ${Tooltip} {
            display: block;
          }
        }

        ${copy.XS.regular}, ${glyphs.Icon} {
          color: ${colors.GREY_SHADE_7};
        }
      `
    if (!disabled)
      return css`
        &:hover {
          ${copy.XS.regular} {
            color: ${colors.PRIMARY_RED_HOVER};
          }
        }
      `
  }}
`

export const SmallText = styled(copy.XS.regular)`
  color: ${colors.TEXT_SECONDARY};
  text-transform: uppercase;
`

export type WrapperProps = { $active: boolean; $errored: boolean; $loading: boolean; disabled: boolean }

const hoveredStyle = css`
  border-color: ${colors.PRIMARY_BLUE_700};
  border-style: solid;
  transition: border 1s cubic-bezier(0.16, 1, 0.3, 1);

  ${Explanations},
  ${Explanations} > ${heading.XXXS},
  ${Explanations} > ${SmallText} {
    color: ${colors.PRIMARY_BLUE_HOVER};
    transition: color 1s cubic-bezier(0.16, 1, 0.3, 1);
  }
`

export const Wrapper = styled.label<WrapperProps>`
  align-items: center;
  border-color: ${colors.GREY_SHADE_5};
  border-radius: 4px;
  border-style: dashed;
  border-width: 1px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  font-family: ${fonts.PRIMARY_FONT};
  justify-content: center;
  max-height: 500px;
  padding: 40px;
  position: relative;
  transition: border 0.4s cubic-bezier(0.22, 1, 0.36, 1);

  ${Explanations},
  ${Explanations} > ${heading.XXXS},
  ${Explanations} > ${SmallText} {
    transition: color 1s cubic-bezier(0.16, 1, 0.3, 1);
  }

  ${Spinner} {
    position: absolute;
    margin: auto;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }

  @media screen and ${breakpoints.TABLET} {
    flex-direction: row;
    padding: 10px 25px;
    height: 220px;
  }

  ${({ $active, disabled }) => {
    if ($active && !disabled) {
      return css`
        ${hoveredStyle};
        cursor: copy;
      `
    }
  }};

  ${({ disabled }) => {
    if (disabled) {
      return css`
        cursor: not-allowed;
        background-color: ${colors.BACKGROUND_DISABLED};
        color: ${colors.PRIMARY_SAND_200};

        ${glyphs.Icon}, ${heading.XXXS}, ${SmallText} {
          color: ${colors.PRIMARY_SAND_200};
        }

        ${RoundedIcon} {
          background-color: ${colors.PRIMARY_WHITE};
        }

        ${RemoveImage} {
          cursor: not-allowed;

          ${copy.XS.regular}, ${glyphs.Icon} {
            color: ${colors.PRIMARY_SAND_200};
          }

          &:hover {
            ${copy.XS.regular}, ${glyphs.Icon} {
              color: ${colors.PRIMARY_SAND_200};
            }
          }
        }
      `
    }

    return css`
      &:hover {
        ${hoveredStyle};
        cursor: pointer;
      }
    `
  }};

  ${({ $errored }) => {
    if ($errored) {
      return css`
        border-color: ${colors.PRIMARY_RED};
      `
    }
  }};

  ${({ $loading }) => {
    if ($loading)
      return css`
        ${Explanations} {
          opacity: 0;
        }

        ${Preview} {
          display: none;
        }
      `
    if (!$loading)
      return css`
        ${Spinner} {
          display: none;
        }
      `
  }};
`

export const RoundedIcon = styled.div`
  align-items: center;
  background-color: ${colors.BLUE_SHADE_3};
  border-radius: 50%;
  color: ${colors.PRIMARY_BLUE};
  display: flex;
  flex: none;
  height: 33px;
  justify-content: center;
  width: 33px;

  ${glyphs.Icon} {
    align-items: center;
    display: flex;
    justify-content: center;
    transform: rotateZ(90deg) translateY(2px);
  }
`

export const InvisibleInput = styled.input`
  height: 0;
  opacity: 0;
  width: 0;
`

/**
 * MINI UPLOADER
 */

export const MiniDeletionOverlay = styled.button`
  align-items: center;
  background-color: ${colors.hexToRgb(colors.PRIMARY_GREY_900, 0.8)};
  border: none;
  border-radius: 4.5px;
  color: ${colors.PRIMARY_GREY_000};
  cursor: pointer;
  display: flex;
  height: 100%;
  justify-content: center;
  position: absolute;
  width: 100%;

  ${glyphs.stroke.Trash} {
    margin-left: 4px;
  }
`

export const MiniPreview = styled.img`
  background-color: ${colors.PRIMARY_GREY_000};
  border-radius: 4px;
  height: 103%;
  object-fit: cover;
  position: absolute;
  width: 104%;
`

export const MiniWrapper = styled(Wrapper)<{ $hasPreview: boolean }>`
  background-color: ${colors.PRIMARY_GREY_000};
  color: ${colors.PRIMARY_SAND_200};
  height: 64px;
  padding: 0;
  width: 64px;

  &:hover {
    color: ${colors.PRIMARY_BLUE_200};

    ${MiniDeletionOverlay} {
      height: 104%;
      width: 104%;
      z-index: 1;
    }
  }

  ${({ $hasPreview = false }) => {
    if ($hasPreview) {
      return css`
        border-color: transparent !important;
      `
    }
  }};

  ${({ disabled = false }) => {
    if (disabled) {
      return css`
        background-color: ${colors.BACKGROUND_DISABLED};
      `
    }
  }};
`
